<template>
  <div class="main-content">
    <div class="card p-2">
      <div class="mb-2 bread">
        WFR <span class="sep2">›</span> Nova solicitação
      </div>

      <div class="d-flex justify-content-between align-items-center mb-3">
        <h4 class="m-0 page-title">Nova solicitação</h4>
      </div>

      <div class="section-card">
        <div class="section-title">Selecionar modelo</div>

        <div class="row mb-3">
          <div class="col-md-12">
            <label class="lbl">Modelo de flow <span class="req">*</span></label>
            <select
              v-model="selectedTemplateId"
              class="form-control"
              @change="applyTemplate"
            >
              <option value="">Selecionar modelo...</option>
              <option
                v-for="tpl in flowTemplates"
                :key="tpl.id"
                :value="tpl.id"
              >
                {{ tpl.title }}{{ tpl.tag ? " — " + tpl.tag : "" }}
              </option>
            </select>
            <div class="help-muted mt-1">
              Ao selecionar o modelo, o nome e as etapas serão preenchidos
              automaticamente.
            </div>
          </div>
        </div>

        <div v-if="selectedTemplateId">
          <div class="row">
            <div class="col-md-6">
              <label class="lbl"
                >Nome da solicitação <span class="req">*</span></label
              >
              <input
                v-model="form.name"
                class="form-control"
                placeholder="Ex: Aprovação de Compras — Maio/2026"
              />
            </div>
            <div class="col-md-6">
              <label class="lbl">Tag</label>
              <input
                v-model="form.tag"
                class="form-control"
                placeholder="Ex: APROVACAO_COMPRAS"
              />
            </div>
          </div>

          <div class="row mt-2">
            <div class="col-md-12">
              <label class="lbl">Descrição <span class="req">*</span></label>
              <textarea
                v-model="form.description"
                class="form-control text-area"
                rows="3"
                placeholder="Descreva o objetivo desta solicitação..."
              ></textarea>
            </div>
          </div>

          <!-- Etapas (somente visualização) -->
          <div class="mt-3">
            <label class="lbl">Etapas do modelo</label>
            <div class="help-muted mb-2">
              Clique em uma etapa para pedir um formulário do responsável
              antes de concluí-la.
            </div>
            <div class="steps-preview">
              <div
                v-for="(s, idx) in steps"
                :key="s.uid"
                class="step-preview-item step-preview-clickable"
                @click="openStepFormModal(idx)"
              >
                <div class="step-num-sm" :class="numClass(idx)">
                  {{ idx + 1 }}
                </div>
                <div class="step-preview-info">
                  <div class="step-preview-name">{{ s.name }}</div>
                  <div class="step-preview-dept">
                    {{ deptName(s.department_id) }}
                  </div>
                  <div v-if="s.form" class="step-form-badge">
                    <i class="fa fa-list-alt"></i>
                    Formulário{{ s.form.form_required ? " obrigatório" : "" }}
                  </div>
                </div>
                <div v-if="idx < steps.length - 1" class="step-preview-arrow">
                  →
                </div>
              </div>
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-md-12">
              <label class="lbl">Anexos</label>
              <div class="upload-box">
                <input
                  ref="pdfFile"
                  type="file"
                  class="d-none"
                  multiple
                  @change="handlePdfFiles"
                />
                <div class="upload-inner">
                  <div class="upload-drop" @click="openPdfPicker">
                    <div class="upload-hint">Clique para anexar arquivos</div>
                    <div class="upload-sub">Selecione um ou mais arquivos</div>
                  </div>
                  <div v-if="pdfFiles.length" class="upload-list">
                    <div
                      v-for="(f, idx) in pdfFiles"
                      :key="idx"
                      class="upload-item"
                    >
                      <div class="file-thumb"><i class="fa fa-file-o"></i></div>
                      <div class="file-meta">
                        <div
                          class="file-name file-link"
                          @click="openSelectedFile(f)"
                        >
                          {{ f.name }}
                        </div>
                        <div class="file-size">{{ prettySize(f.size) }}</div>
                      </div>
                      <button
                        class="btn btn-light btn-sm"
                        type="button"
                        @click="openSelectedFile(f)"
                      >
                        <i class="fa fa-external-link"></i> Abrir
                      </button>
                      <button
                        class="btn btn-light btn-sm"
                        type="button"
                        @click="removePdf(idx)"
                      >
                        <i class="fa fa-trash"></i> Excluir
                      </button>
                    </div>
                  </div>
                  <div v-if="pdfError" class="alert alert-danger mt-2 p-2">
                    {{ pdfError }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="error" class="alert alert-danger mt-2 p-2">{{ error }}</div>

        <div
          class="d-flex justify-content-end align-items-center mt-3"
          style="gap: 10px"
        >
          <button class="btn btn-outline-secondary" @click="cancel">
            Voltar
          </button>
          <button
            class="btn btn-primary"
            :disabled="!selectedTemplateId || saving"
            @click="saveFlow"
          >
            {{ saving ? "Enviando..." : "Abrir solicitação" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: formulário da etapa -->
    <div
      v-if="stepFormModal.open"
      class="modal-mask"
      @click.self="closeStepFormModal"
    >
      <div class="modal-card">
        <div class="modal-head">
          <div class="modal-title">
            Formulário da etapa —
            {{ stepFormModal.stepIdx !== null ? steps[stepFormModal.stepIdx].name : "" }}
          </div>
          <button class="modal-x" @click="closeStepFormModal">×</button>
        </div>
        <div class="modal-body">
          <div class="d-flex align-items-center mb-3" style="gap: 10px">
            <input
              id="formRequired"
              type="checkbox"
              v-model="stepFormModal.form_required"
            />
            <label for="formRequired" class="m-0 lbl" style="cursor: pointer">
              Responder este formulário é obrigatório para concluir a etapa
            </label>
          </div>

          <div class="mode-tabs mb-3">
            <button
              class="mode-tab"
              :class="{ active: stepFormModal.mode === 'existing' }"
              @click="stepFormModal.mode = 'existing'"
            >
              Usar modelo existente
            </button>
            <button
              class="mode-tab"
              :class="{ active: stepFormModal.mode === 'scratch' }"
              @click="stepFormModal.mode = 'scratch'"
            >
              Criar do zero
            </button>
          </div>

          <div v-if="stepFormModal.mode === 'existing'" class="mb-3">
            <label class="lbl">Modelo de formulário</label>
            <select
              v-model="stepFormModal.form_template_id"
              class="form-control"
              @change="applyFormTemplate"
            >
              <option value="">Selecionar modelo...</option>
              <option
                v-for="tpl in formTemplates"
                :key="tpl.id"
                :value="tpl.id"
              >
                {{ tpl.title }}
              </option>
            </select>
          </div>

          <question-builder v-model="stepFormModal.questions" />

          <div v-if="stepFormError" class="alert alert-danger mt-2 p-2">
            {{ stepFormError }}
          </div>
        </div>
        <div class="modal-foot">
          <button
            v-if="stepFormModal.stepIdx !== null && steps[stepFormModal.stepIdx].form"
            class="btn btn-outline-danger mr-auto"
            @click="removeStepForm"
          >
            Remover formulário
          </button>
          <button class="btn btn-outline-secondary" @click="closeStepFormModal">
            Cancelar
          </button>
          <button class="btn btn-primary" @click="saveStepFormModal">
            Salvar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QuestionBuilder from "./FormTemplates/QuestionBuilder.vue";

export default {
  components: { QuestionBuilder },

  data() {
    return {
      error: "",
      saving: false,
      pdfError: "",
      pdfFiles: [],

      flowTemplates: [],
      selectedTemplateId: "",

      departments: [],
      formTemplates: [],

      form: {
        name: "",
        tag: "",
        owner_department_id: "",
        description: "",
      },

      steps: [],

      stepFormModal: {
        open: false,
        stepIdx: null,
        form_required: false,
        mode: "existing",
        form_template_id: "",
        questions: [],
      },
      stepFormError: "",
    };
  },

  async mounted() {
    await Promise.all([
      this.loadMeta(),
      this.loadTemplates(),
      this.loadFormTemplates(),
    ]);
  },

  methods: {
    async loadFormTemplates() {
      try {
        const apiBase = this.$store.state.api;
        const res = await this.$http.get(apiBase + "form-templates", {
          params: { paginated: false },
        });
        const raw = res.data;
        this.formTemplates = Array.isArray(raw) ? raw : raw.data || [];
      } catch (e) {
        console.error(e);
        this.formTemplates = [];
      }
    },

    openStepFormModal(idx) {
      const existing = this.steps[idx].form;

      this.stepFormModal = {
        open: true,
        stepIdx: idx,
        form_required: existing ? !!existing.form_required : false,
        mode: existing && existing.form_template_id ? "existing" : "scratch",
        form_template_id: existing ? existing.form_template_id || "" : "",
        questions: existing ? existing.questions.map((q) => ({ ...q })) : [],
      };
      this.stepFormError = "";
    },

    closeStepFormModal() {
      this.stepFormModal.open = false;
      this.stepFormError = "";
    },

    applyFormTemplate() {
      const tpl = this.formTemplates.find(
        (t) => t.id === this.stepFormModal.form_template_id
      );
      if (!tpl) return;

      this.stepFormModal.questions = (tpl.questions || [])
        .slice()
        .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
        .map((q) => ({
          label: q.label,
          field_type: q.field_type,
          options: q.options || [],
          required: !!q.required,
        }));
    },

    saveStepFormModal() {
      const { stepIdx, mode, form_required, form_template_id, questions } =
        this.stepFormModal;

      if (!questions.length) {
        this.stepFormError = "Adicione ao menos uma pergunta ao formulário.";
        return;
      }

      this.$set(this.steps[stepIdx], "form", {
        form_required: !!form_required,
        form_template_id: mode === "existing" ? form_template_id || null : null,
        questions: questions.map((q) => ({
          label: q.label,
          field_type: q.field_type,
          options: q.options || [],
          required: !!q.required,
        })),
      });

      this.closeStepFormModal();
    },

    removeStepForm() {
      const idx = this.stepFormModal.stepIdx;
      if (idx === null) return;
      this.$set(this.steps[idx], "form", null);
      this.closeStepFormModal();
    },

    async loadMeta() {
      try {
        const apiBase = this.$store.state.api;
        const res = await this.$http.get(
          apiBase + "departments?paginated=false"
        );
        this.departments = res.data.data || [];
      } catch (e) {
        console.error(e);
      }
    },

    async loadTemplates() {
      try {
        const apiBase = this.$store.state.api;
        const res = await this.$http.get(apiBase + "flow-templates", {
          params: { paginated: false },
        });
        const raw = res.data;
        this.flowTemplates = Array.isArray(raw) ? raw : raw.data || [];
      } catch (e) {
        console.error(e);
        this.flowTemplates = [];
      }
    },

    async applyTemplate() {
      if (!this.selectedTemplateId) return;

      try {
        const apiBase = this.$store.state.api;
        const res = await this.$http.get(
          apiBase + "flow-templates/" + this.selectedTemplateId
        );
        const tpl = res.data;

        if (!tpl) return;

        this.form.name = tpl.title || "";
        this.form.tag = tpl.tag || "";
        this.form.description = "";
        this.form.owner_department_id = "";

        const transitions = tpl.transitions || [];

        this.steps = transitions.length
          ? transitions
              .slice()
              .sort(
                (a, b) => Number(a.step_order || 0) - Number(b.step_order || 0)
              )
              .map((s, index) => ({
                uid: `tpl_${s.id || index}_${Date.now()}`,
                name: s.name || `Etapa ${index + 1}`,
                department_id: s.department_id || "",
                requires_approval: !!s.requires_approval,
                approver_user_id: s.approver_user_id || "",
                form: s.form_template_id
                  ? {
                      form_required: !!s.form_required,
                      form_template_id: s.form_template_id,
                      questions: (s.form_template?.questions || [])
                        .slice()
                        .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
                        .map((q) => ({
                          label: q.label,
                          field_type: q.field_type,
                          options: q.options || [],
                          required: !!q.required,
                        })),
                    }
                  : null,
              }))
          : [
              {
                uid: "s1",
                name: "Abertura",
                department_id: "",
                requires_approval: false,
                approver_user_id: "",
                form: null,
              },
            ];

        // Set owner_department_id from first step with department
        const firstWithDept = this.steps.find((s) => s.department_id);
        if (firstWithDept) {
          this.form.owner_department_id = firstWithDept.department_id;
        }
      } catch (e) {
        console.error(e);
        this.error = "Não consegui carregar o modelo selecionado.";
      }
    },

    deptName(id) {
      if (!id) return "";
      const d = this.departments.find((x) => x.id == id);
      return d ? d.name : "";
    },

    numClass(idx) {
      if (idx === 0) return "n-blue";
      if (idx === 1) return "n-green";
      return "n-gray";
    },

    validate() {
      this.error = "";
      if (!this.selectedTemplateId) {
        swal("Atenção", "Selecione um modelo de flow.", "warning");
        return false;
      }
      if (!this.form.name?.trim()) {
        swal("Atenção", "Informe o nome da solicitação.", "warning");
        return false;
      }
      if (!this.form.description?.trim()) {
        swal("Atenção", "A descrição é obrigatória.", "warning");
        return false;
      }
      if (!this.steps.length) {
        swal("Atenção", "O modelo não possui etapas configuradas.", "warning");
        return false;
      }
      return true;
    },

    openPdfPicker() {
      this.$refs.pdfFile.click();
    },

    handlePdfFiles(e) {
      this.pdfError = "";
      Array.from(e.target.files || []).forEach((f) => this.pdfFiles.push(f));
      e.target.value = "";
    },

    removePdf(idx) {
      this.pdfFiles.splice(idx, 1);
    },

    prettySize(bytes) {
      if (!bytes && bytes !== 0) return "";
      const kb = bytes / 1024;
      if (kb < 1024) return `${kb.toFixed(2)} KB`;
      return `${(kb / 1024).toFixed(2)} MB`;
    },

    openSelectedFile(file) {
      try {
        const blobUrl = window.URL.createObjectURL(file);
        window.open(blobUrl, "_blank");
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
      } catch (e) {
        console.error(e);
        this.pdfError = "Não consegui abrir o arquivo selecionado.";
      }
    },

    getLoggedUserId() {
      return (
        this.$store?.state?.user?.id ||
        this.$store?.state?.auth?.user?.id ||
        this.$store?.state?.userData?.id ||
        null
      );
    },

    async saveFlow() {
      if (!this.validate()) return;
      if (this.saving) return;

      this.saving = true;

      try {
        this.error = "";
        const openedByUserId = this.getLoggedUserId();

        const steps = this.steps.map((s, i) => ({
          order: i + 1,
          name: s.name || `Etapa ${i + 1}`,
          department_id: s.department_id || null,
          requires_approval: !!s.requires_approval,
          approver_user_id: s.requires_approval
            ? s.approver_user_id || null
            : null,
          form: s.form
            ? {
                form_required: !!s.form.form_required,
                form_template_id: s.form.form_template_id || null,
                questions: s.form.questions,
              }
            : null,
        }));

        const fd = new FormData();
        fd.append("title", this.form.name || "");
        fd.append("tag", this.form.tag || "");
        fd.append("description", this.form.description || "");
        fd.append("owner_department_id", this.form.owner_department_id || "");
        if (openedByUserId) fd.append("opened_by_user_id", openedByUserId);
        fd.append("steps", JSON.stringify(steps));
        for (const f of this.pdfFiles) fd.append("attachments[]", f);

        const apiBase = this.$store.state.api;
        const { data } = await this.$http.post(apiBase + "flows", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        swal(
          "Sucesso",
          "Solicitação aberta com sucesso! ID: " + data.id,
          "success"
        ).then(() => {
          this.$router.push("/Flow/my");
        });
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data ||
          "Erro ao abrir a solicitação.";
        this.error = typeof msg === "string" ? msg : JSON.stringify(msg);
        swal("Erro", this.error, "error");
      } finally {
        this.saving = false;
      }
    },

    cancel() {
      this.$router.push("/Flow");
    },
  },
};
</script>

<style scoped>
.bread {
  font-size: 12px;
  color: #6b7280;
  font-weight: 700;
}
.sep2 {
  margin: 0 6px;
  color: #9ca3af;
}
.page-title {
  font-weight: 700;
  color: #111827;
}
.form-control {
  height: 38px;
}
.text-area {
  height: auto;
  min-height: 88px;
  resize: vertical;
  padding-top: 10px;
}
.section-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px;
}
.section-title {
  font-size: 16px;
  font-weight: 800;
  color: #111827;
  margin-bottom: 12px;
}
.lbl {
  font-weight: 800;
  color: #374151;
  margin-bottom: 6px;
  display: block;
}
.req {
  color: #dc2626;
}
.help-muted {
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
}
.upload-box {
  border: 1px dashed #cbd5e1;
  background: rgba(255, 255, 255, 0.65);
  border-radius: 12px;
  padding: 12px;
}
.upload-inner {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.upload-drop {
  cursor: pointer;
  border-radius: 10px;
  padding: 10px;
  background: rgba(243, 244, 246, 0.6);
  border: 1px solid #e5e7eb;
}
.upload-hint {
  font-weight: 800;
  color: #374151;
}
.upload-sub {
  font-size: 12px;
  color: #6b7280;
}
.upload-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.upload-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.85);
}
.file-thumb {
  width: 44px;
  height: 34px;
  border-radius: 8px;
  background: linear-gradient(135deg, #e5e7eb, #f3f4f6);
  border: 1px solid #e5e7eb;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}
.file-meta {
  flex: 1;
}
.file-name {
  font-weight: 800;
  color: #111827;
}
.file-size {
  font-size: 12px;
  color: #6b7280;
}
.file-link {
  cursor: pointer;
  color: #1d4ed8;
  text-decoration: underline;
}
.steps-preview {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  background: #fafafa;
  border: 1px dashed #e5e7eb;
  border-radius: 10px;
  padding: 12px;
}
.step-preview-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.step-preview-clickable .step-preview-info {
  cursor: pointer;
}
.step-preview-clickable .step-preview-info:hover {
  border-color: #93c5fd;
  box-shadow: 0 1px 8px rgba(59, 130, 246, 0.12);
}
.step-form-badge {
  margin-top: 4px;
  font-size: 10px;
  font-weight: 800;
  color: #1d4ed8;
  white-space: nowrap;
}
.step-num-sm {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 900;
  font-size: 11px;
  flex-shrink: 0;
}
.n-blue {
  background: #3b82f6;
}
.n-green {
  background: #22c55e;
}
.n-gray {
  background: #9ca3af;
}
.step-preview-info {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 10px;
  min-width: 120px;
}
.step-preview-name {
  font-weight: 800;
  color: #111827;
  font-size: 13px;
}
.step-preview-dept {
  font-size: 11px;
  color: #6b7280;
}
.step-preview-arrow {
  color: #9ca3af;
  font-weight: 900;
  margin: 0 4px;
}

/* Modal: formulário da etapa */
.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 18px;
}
.modal-card {
  width: 640px;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 16px 60px rgba(0, 0, 0, 0.18);
}
.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #eef2f7;
  position: sticky;
  top: 0;
  background: #fff;
}
.modal-title {
  font-weight: 900;
  color: #111827;
}
.modal-x {
  border: 0;
  background: transparent;
  font-size: 22px;
  font-weight: 900;
  color: #6b7280;
  cursor: pointer;
}
.modal-body {
  padding: 14px 16px;
}
.modal-foot {
  padding: 14px 16px;
  border-top: 1px solid #eef2f7;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.mode-tabs {
  display: flex;
  gap: 8px;
}
.mode-tab {
  flex: 1;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  font-weight: 700;
  color: #6b7280;
  cursor: pointer;
}
.mode-tab.active {
  border-color: #93c5fd;
  background: #eff6ff;
  color: #1d4ed8;
}
</style>
